import { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/ui/table";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Search, Plus, MoreHorizontal, FileText, Receipt, MailCheck, FileCheck } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "src/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/components/ui/tooltip";
import newDashEmptyGraphic from "../../../assets/images/new-dash-empty.png";
import { useGetInsertionOrdersQuery, useDeleteInsertionOrderMutation, useCloneInsertionOrderMutation } from "../../../redux/billing/billing.endpoints";
import { InsertionOrder, BillingItem } from "../../../redux/billing/billing.types";
import { InsertionOrderModal } from "./create-insertion-order-modal";
import { CreateInvoiceModal } from "./create-invoice-modal";

interface StatusTooltipIconProps {
  icon: React.ComponentType<any>;
  isActive: boolean;
  tooltipMessage: string;
  className?: string;
}
const StatusTooltipIcon = ({ icon: Icon, isActive, tooltipMessage, className = "" }: StatusTooltipIconProps) => (
  <TooltipProvider delayDuration={0}>
    <Tooltip>
      <TooltipTrigger>
        <Icon
          className={`w-4 h-4 ${className} ${isActive ? 'text-blue-500/75' : 'text-gray-400'}`}
          strokeWidth={2.5}
        />
      </TooltipTrigger>
      <TooltipContent className="px-3 py-1">
        <p className="text-xs">
          {tooltipMessage}
        </p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

const BillingTable = () => {
  const [searchValue, setSearchValue] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'insertion-orders' | 'invoices'>('all');
  const [isInsertionOrderModalOpen, setIsInsertionOrderModalOpen] = useState(false);
  const [isInvoiceModalOpen, setIsInvoiceModalOpen] = useState(false);
  const [selectedInsertionOrder, setSelectedInsertionOrder] = useState<InsertionOrder | undefined>(undefined);
  const [modalMode, setModalMode] = useState<'create' | 'edit' | 'view'>('create');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);

  const { data: insertionOrdersData, isLoading: insertionOrdersLoading, refetch } = useGetInsertionOrdersQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [deleteInsertionOrder, { isLoading: isDeleting }] = useDeleteInsertionOrderMutation();
  const [cloneInsertionOrder, { isLoading: isCloning }] = useCloneInsertionOrderMutation();

  const parseLocalDate = (dateString: string): Date => {
    // If the date string is just YYYY-MM-DD format, parse it as local date
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number);
      return new Date(year, month - 1, day); // month is 0-indexed in Date constructor
    }
    // For full timestamps, use regular Date parsing
    return new Date(dateString);
  };

  const insertionOrderItems: BillingItem[] = (insertionOrdersData || [])
    .map((order: InsertionOrder) => {
      let status: 'draft' | 'sent' | 'completed' = 'draft';
      if (order.client_signed && order.agency_signed) {
        status = 'completed';
      } else if (order.is_active) {
        status = 'sent';
      }
      return {
        id: order.id,
        type: 'insertion-order' as const,
        date: new Date(order.updated_at),
        advertiser: order.advertiser_name || 'N/A',
        campaign: order.campaign_name || 'N/A',
        startDate: parseLocalDate(order.start_date),
        endDate: parseLocalDate(order.end_date),
        owner: order.agent_name,
        status,
        clientDelivered: order.client_delivered || order.client_signed,
        clientSigned: order.client_signed,
        agencyDelivered: order.agency_delivered || order.agency_signed,
        agencySigned: order.agency_signed,
        clientEmail: order.client_email,
        agentEmail: order.agent_email,
      };
    })
    .sort((a, b) => b.date.getTime() - a.date.getTime());

  // TODO: Also add invoices into billing table once invoice API is available
  const allBillingData = insertionOrderItems;

  const handleInsertionOrderClick = (item: BillingItem) => {
    const fullOrder = insertionOrdersData?.find(order => order.id === item.id);
    if (!fullOrder) return;
    setSelectedInsertionOrder(fullOrder);
    if (item.status === 'draft') {
      setModalMode('edit');
    } else {
      setModalMode('view');
    }
    setIsInsertionOrderModalOpen(true);
  };

  const handleCreateNewInsertionOrder = () => {
    setSelectedInsertionOrder(undefined);
    setModalMode('create');
    setIsInsertionOrderModalOpen(true);
  };

  const getStatusColor = (status: string, isDot: boolean = false) => {
    switch (status) {
      case 'draft':
        return isDot ? 'bg-gray-600' : 'text-gray-500';
      case 'sent':
        return isDot ? 'bg-blue-500' : 'text-blue-500';
      case 'completed':
        return isDot ? 'bg-[#31BB64]' : 'text-[#31BB64]';
      default:
        return isDot ? 'bg-gray-600' : 'text-gray-600';
    }
  };

  const getTypeIcon = (type: string) => {
    if (type === 'insertion-order') {
      return (
        <div className="flex items-center gap-1">
          <FileText className="w-3 h-3" />
          IO
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-1">
          <Receipt className="w-3 h-3" />
          Invoice
        </div>
      );
    }
  };

  const renderStatusBreakdown = (item: BillingItem) => {
    if (item.status !== 'sent') {
      return (
        <span
          className={`inline-flex items-center justify-center rounded-full py-1 px-2 text-xs font-bold bg-white shadow-sm ${getStatusColor(item.status)} ${item.status === 'completed' ? 'min-w-[100px]' : ''}`}
        >
          <div className={`size-1 rounded-full mr-1 ${getStatusColor(item.status, true)}`} />
          {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
        </span>
      );
    }

    return (
      <span className="inline-flex items-center rounded-full py-1 px-2 bg-white shadow-sm">
        <div className="flex items-center gap-1">
          {/* Client Section */}
          <div className="flex flex-col items-center gap-0.5 mr-1">
            <div className="flex items-center gap-1">
              <StatusTooltipIcon
                icon={MailCheck}
                isActive={item.clientDelivered}
                tooltipMessage={item.clientDelivered ? `Delivered to ${item.clientEmail}` : `Awaiting delivery to ${item.clientEmail}`}
              />
              <StatusTooltipIcon
                icon={FileCheck}
                isActive={item.clientSigned}
                tooltipMessage={item.clientSigned ? `Signed by ${item.clientEmail}` : `Awaiting signature from ${item.clientEmail}`}
              />
            </div>
            <span className="text-[8px] text-black/50 font-medium leading-none">
              Client
            </span>
          </div>

          {/* Agent Section */}
          <div className="flex flex-col items-center gap-0.5 mr-1">
            <div className="flex items-center gap-1">
              <StatusTooltipIcon
                icon={MailCheck}
                isActive={item.agencyDelivered}
                tooltipMessage={item.agencyDelivered ? `Delivered to ${item.agentEmail}` : `Awaiting delivery to ${item.agentEmail}`}
              />
              <StatusTooltipIcon
                icon={FileCheck}
                isActive={item.agencySigned}
                tooltipMessage={item.agencySigned ? `Signed by ${item.agentEmail}` : `Awaiting signature from ${item.agentEmail}`}
              />
            </div>
            <span className="text-[8px] text-black/50 font-medium leading-none">
              Internal
            </span>
          </div>
        </div>
      </span>
    );
  };

  const formatFlightDates = (startDate: Date, endDate: Date) => {
    const currentYear = new Date().getFullYear();
    const startYear = startDate.getFullYear();
    const endYear = endDate.getFullYear();

    const formatDate = (date: Date, includeYear: boolean = true) => {
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      if (!includeYear) {
        return `${month}/${day}`;
      }
      const year = date.getFullYear().toString().slice(-2);
      return `${month}/${day}/${year}`;
    };
    return `${formatDate(startDate, startYear !== currentYear)}-${formatDate(endDate, endYear !== currentYear)}`;
  };

  const filteredData = allBillingData.filter(item => {
    const matchesFilter = activeFilter === 'all' ||
      (activeFilter === 'insertion-orders' && item.type === 'insertion-order') ||
      (activeFilter === 'invoices' && item.type === 'invoice');

    const matchesSearch = item.advertiser.toLowerCase().includes(searchValue.toLowerCase()) ||
      item.campaign.toLowerCase().includes(searchValue.toLowerCase()) ||
      item.owner.toLowerCase().includes(searchValue.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  const isEmpty = filteredData.length === 0;
  const hasSearchOrFilter = searchValue.trim() !== '' || activeFilter !== 'all';

  const handleDeleteInsertionOrder = async(id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await deleteInsertionOrder(id).unwrap();
      setShowDeleteDialog(false);
      setItemToDelete(null);
      refetch();
    } catch (error) {
      console.error('Failed to delete insertion order:', error);
    }
  };

  const handleDeleteClick = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setItemToDelete(id);
    setShowDeleteDialog(true);
  };

  const handleCloneInsertionOrder = async(id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await cloneInsertionOrder(id).unwrap();
    } catch (error) {
      console.error('Failed to clone insertion order:', error);
    }
  };

  if (insertionOrdersLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="text-gray-500">
          Loading billing data...
        </div>
      </div>
    );
  }

  const renderEmptyState = () => {
    const isSearchEmpty = hasSearchOrFilter;

    return (
      <div className="flex flex-col h-full items-center justify-center bg-white border border-gray-200 rounded-xl py-4 sm:py-32">
        <img
          src={newDashEmptyGraphic}
          className="w-40 h-20"
          alt="Empty state"
        />
        {isSearchEmpty ? (
          <>
            <div className="text-3xl font-light mt-10">
              No billing items found.
            </div>
            <div>
              Try changing your search criteria or filters.
            </div>
          </>
        ) : (
          <>
            <div className="flex flex-col items-center mt-10 mb-6">
              <div className="text-3xl font-light">
                No billing items yet.
              </div>
              <div>
                Create your first insertion order or invoice to get started.
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  const renderFilterButton = (filter: typeof activeFilter, label: string) => (
    <Button
      size='med'
      variant={activeFilter === filter ? 'primary' : 'outline'}
      onClick={() => setActiveFilter(filter)}
      key={filter}
    >
      {label}
    </Button>
  );

  return (
    <div className="flex flex-col gap-4 grow min-h-0">
      {/* Filter Controls */}
      <div className="flex flex-col flex-wrap md:flex-row sm:justify-between items-center gap-5 flex-shrink-0">
        <div className="flex flex-row gap-2 justify-end">
          {renderFilterButton('all', 'All')}
          {renderFilterButton('insertion-orders', 'Insertion Orders')}
          {renderFilterButton('invoices', 'Invoices')}
        </div>
        <div className="h-8 border-l-1 border-black/10 -mx-1 hidden md:block" />
        <div className="flex flex-row gap-2">
          <Button
            size='med'
            variant='outline'
            onClick={handleCreateNewInsertionOrder}
            className="flex items-center gap-2 bg-green-1000 text-white border-green-1000 hover:bg-green-1000/90 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-[1.02]"
          >
            <Plus className="h-4 w-4" />
            Insertion Order
          </Button>
          <Button
            size='med'
            variant='outline'
            onClick={() => setIsInvoiceModalOpen(true)}
            className="flex items-center gap-2 bg-green-1000 text-white border-green-1000 hover:bg-green-1000/90 shadow-md hover:shadow-lg transition-all duration-200 hover:scale-[1.02]"
          >
            <Plus className="h-4 w-4" />
            Invoice
          </Button>
        </div>
        <div className="relative flex-1 md:order-first w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            type="text"
            placeholder="Search"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="pl-10 bg-white rounded-xl placeholder:text-black/75 shadow-sm h-10"
          />
        </div>
      </div>

      {/* Billing Table */}
      <div className="flex-grow min-h-0 flex flex-col">
        {isEmpty ? (
          renderEmptyState()
        ) : (
          <div className="w-full bg-white rounded-xl border border-gray-200 p-4 pt-2 flex-grow min-h-0 flex flex-col">
            <div className="flex-grow min-h-0 overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow className="border-none">
                    <TableHead className="w-[78px] max-w-[78px] py-1.5" />
                    <TableHead className="text-gray-600 py-1.5">
                      Last Updated
                    </TableHead>
                    <TableHead className="text-gray-600 py-1.5">
                      Advertiser
                    </TableHead>
                    <TableHead className="text-gray-600 py-1.5">
                      Campaign
                    </TableHead>
                    <TableHead className="text-gray-600 py-1.5">
                      Flight Dates
                    </TableHead>
                    <TableHead className="text-gray-600 py-1.5">
                      Owner
                    </TableHead>
                    <TableHead className="text-gray-600 py-1.5 text-center">
                      Status
                    </TableHead>
                    <TableHead className="w-[30px] py-1.5" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.map((item, index) => (
                    <TableRow
                      key={item.id}
                      className={`hover:bg-gray-100 transition-colors border-b border-gray-100 last:border-b-0 ${
                        index % 2 === 0 ? 'bg-[#F8F9FA]' : 'bg-[#F2F3F5]'
                      } cursor-pointer`}
                      onClick={() => item.type === 'insertion-order' && handleInsertionOrderClick(item)}
                    >
                      <TableCell className={`text-center py-1.5 ${index === 0 ? 'rounded-tl-xl' : index === filteredData.length - 1 ? 'rounded-bl-xl' : ''}`}>
                        <span className="max-w-[78px] inline-flex items-center justify-center rounded-full py-1 px-2 text-xs font-bold bg-white shadow-sm text-gray-700">
                          {getTypeIcon(item.type)}
                        </span>
                      </TableCell>
                      <TableCell className="py-1.5">
                        {item.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </TableCell>
                      <TableCell className="py-1.5">
                        {item.advertiser}
                      </TableCell>
                      <TableCell className="py-1.5">
                        {item.campaign}
                      </TableCell>
                      <TableCell className="py-1.5">
                        {formatFlightDates(item.startDate, item.endDate)}
                      </TableCell>
                      <TableCell className="py-1.5">
                        {item.owner}
                      </TableCell>
                      <TableCell className="py-1.5 text-center">
                        {renderStatusBreakdown(item)}
                      </TableCell>
                      <TableCell
                        className={`${index === 0 ? 'rounded-tr-xl' : index === filteredData.length - 1 ? 'rounded-br-xl' : ''} py-1.5`}
                        onClick={e => e.stopPropagation()}
                      >
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              disabled={isDeleting || isCloning}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              className="cursor-pointer"
                              onClick={(e) => handleCloneInsertionOrder(item.id, e)}
                              disabled={isCloning}
                            >
                              {isCloning ? 'Cloning...' : 'Clone'}
                            </DropdownMenuItem>
                            <Dialog
                              open={showDeleteDialog}
                              onOpenChange={setShowDeleteDialog}
                            >
                              <DialogTrigger asChild>
                                <DropdownMenuItem
                                  className="cursor-pointer"
                                  onClick={(e) => handleDeleteClick(item.id, e)}
                                  disabled={isDeleting}
                                  onSelect={(e) => e.preventDefault()}
                                >
                                  Delete
                                </DropdownMenuItem>
                              </DialogTrigger>
                              <DialogContent
                                onClick={e => e.stopPropagation()}
                                className="bg-white"
                              >
                                <DialogHeader>
                                  <DialogTitle>
                                    Delete Insertion Order
                                  </DialogTitle>
                                  <DialogDescription>
                                    Are you sure you want to delete this insertion order? This action cannot be undone.
                                  </DialogDescription>
                                </DialogHeader>
                                <DialogFooter>
                                  <Button
                                    variant="outline"
                                    onClick={() => setShowDeleteDialog(false)}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    className="bg-red-600 text-white hover:bg-red-700"
                                    onClick={(e) => itemToDelete && handleDeleteInsertionOrder(itemToDelete, e)}
                                    disabled={isDeleting}
                                  >
                                    {isDeleting ? 'Deleting...' : 'Delete'}
                                  </Button>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      <InsertionOrderModal
        open={isInsertionOrderModalOpen}
        onOpenChange={setIsInsertionOrderModalOpen}
        initialData={selectedInsertionOrder}
        mode={modalMode}
        onSuccess={() => refetch()}
      />
      <CreateInvoiceModal
        open={isInvoiceModalOpen}
        onOpenChange={setIsInvoiceModalOpen}
      />
    </div>
  );
};

export default BillingTable;